import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_SERVICE } from '@auth/auth.token';
import { IAuthService } from '@auth/interfaces/auth-service.interface';
import { AppRootRoute } from '@core/enums/app-route.enum';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { from, lastValueFrom, Observable } from 'rxjs';

const silentPaths = [
  '/api/notifications/has-unread',
  '/api/profile/[^/]+/photo',
];

/**
 * Interceptor to add Okta access token to HTTP requests.
 */
@Injectable()
export class OktaInterceptor implements HttpInterceptor {
  constructor(
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    @Inject(AUTH_SERVICE) private readonly authService: IAuthService,
    private readonly router: Router
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return from(this.handleAccess(request, next));
  }

  private async handleAccess(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Promise<HttpEvent<unknown>> {
    // Only add token to API requests (not to asset requests or external URLs)
    const isApiRequest = request.url.includes('/api/');

    if (!isApiRequest) return await lastValueFrom(next.handle(request));

    try {
      const accessToken = this.oktaAuth.getAccessToken();

      if (accessToken) {
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
    } catch (error) {
      console.error('Error getting access token:', error);
      this.authService.logout();

      throw error;
    }

    try {
      return await lastValueFrom(next.handle(request));
    } catch (error: any) {
      // Silently handle errors for specific paths
      if (silentPaths.some(path => new RegExp(path).test(request.url))) {
        throw error;
      }

      if (error.status === HttpStatusCode.Forbidden) {
        this.router.navigate([AppRootRoute.ERRORS, AppRootRoute.FORBIDDEN]);
      }

      throw error;
    }
  }
}
